'use client';
import { memo, useCallback, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import debounce from 'lodash.debounce';

import { Button } from '@core/design-system/button';
import { ISeriesHiddenProps } from './interface';
import { HiOutlineChevronUp } from '@react-icons/all-files/hi/HiOutlineChevronUp';
import { HiOutlineChevronDown } from '@react-icons/all-files/hi/HiOutlineChevronDown';

export const SeriesHidden = memo<ISeriesHiddenProps>(function SeriesHidden({
  assets,
  onShowAssets,
  color = 'primary',
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const delayed = useCallback(debounce(onShowAssets, 100), [onShowAssets]);

  useEffect(() => {
    return delayed.cancel;
  }, [delayed]);

  const toggleAllSeries = () => {
    const isAllVisible = assets?.every((item) => item.visible);

    const newSelectedSeries = assets?.map((item) => ({
      ...item,
      visible: !isAllVisible,
    }));

    delayed(newSelectedSeries);
  };

  const onSelectSerie = ({
    selectedSerieName,
  }: {
    selectedSerieName: string;
  }) => {
    if (!selectedSerieName) return;

    const newSelectedSeries = assets?.map((item) => {
      if (item?.name === selectedSerieName) {
        return {
          ...item,
          visible: !item.visible,
        };
      }
      return item;
    });

    delayed(newSelectedSeries!);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isChecked = (serieName: string) => {
    return assets?.find((item) => item?.name === serieName)?.visible ?? false;
  };

  return (
    <>
      <Button
        variant="outlined"
        color={color}
        size="small"
        id="series-hidden"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{
          minWidth: 140,
          fontWeight: 500,
          borderColor: '#E3E3E3',
          height: 32,
        }}
        endIcon={open ? <HiOutlineChevronUp /> : <HiOutlineChevronDown />}
      >
        Exibir ativos
      </Button>

      <Menu
        id="benchmark-menu"
        data-testid="benchmark-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: '400px',
          },
        }}
      >
        <Box>
          <MenuItem
            sx={{ padding: '3px 10px' }}
            value="select-all"
            onClick={() => toggleAllSeries()}
          >
            <Checkbox
              checked={assets?.every((item) => item.visible === true)}
              id="select-all"
            />
            <ListItemText primary="Selecionar todos" />
          </MenuItem>
        </Box>
        {assets?.length > 0 &&
          assets?.map((value, _) => (
            <Box key={`${value?.name}`}>
              <MenuItem
                sx={{ padding: '3px 10px' }}
                value={value?.name}
                onClick={() =>
                  onSelectSerie({ selectedSerieName: value?.name })
                }
              >
                <Checkbox checked={isChecked(value?.name)} id={value?.name} />
                <ListItemText primary={value?.name} />
              </MenuItem>
            </Box>
          ))}
      </Menu>
    </>
  );
});
